import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  Package, 
  ShoppingCart, 
  DollarSign, 
  TrendingUp, 
  Eye,
  Plus,
  Settings,
  BarChart3,
  Image
} from 'lucide-react';

import { adminAPI } from '../../services/api';

const DashboardPage = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProducts: 0,
    totalOrders: 0,
    totalRevenue: 0
  });

  const [recentOrders, setRecentOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [lowStock, setLowStock] = useState({ count: 0, products: [] });

  useEffect(() => {
    const loadDashboardData = async () => {
      setIsLoading(true);
      try {
        const response = await adminAPI.getDashboardStats();
        const data = response?.data || {};
        const apiStats = data.stats || {};

        setStats({
          totalUsers: Number(apiStats.totalUsers) || 0,
          totalProducts: Number(apiStats.totalProducts) || 0,
          totalOrders: Number(apiStats.totalOrders) || 0,
          totalRevenue: Number(apiStats.totalRevenue) || 0
        });

        const orders = (data.recentOrders || []).map((o) => ({
          id: o.id,
          orderNumber: o.order_number,
          customerName: `${o.first_name || ''} ${o.last_name || ''}`.trim(),
          amount: Number(o.total_amount) || 0,
          status: o.status,
          date: o.created_at
        }));
        setRecentOrders(orders);

        // Save low stock list
        setLowStock({
          count: Number(data?.lowStock?.count || 0),
          products: Array.isArray(data?.lowStock?.products) ? data.lowStock.products : []
        });
      } catch (error) {
        console.error('Failed to load dashboard data', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-500';
      case 'processing': return 'bg-yellow-500';
      case 'shipped': return 'bg-blue-500';
      case 'delivered': return 'bg-green-600';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmed': return 'Confirmé';
      case 'processing': return 'En traitement';
      case 'shipped': return 'Expédié';
      case 'delivered': return 'Livré';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">
            Tableau de Bord
          </h1>
          <p className="text-gray-400 mt-2">
            Bienvenue dans votre espace d'administration TOLOU
          </p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-gray-800 rounded-xl p-6 border border-gray-700"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Utilisateurs</p>
              <p className="text-2xl font-bold text-white">{stats.totalUsers}</p>
            </div>
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Users size={24} className="text-blue-500" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp size={16} className="text-green-500 mr-1" />
            <span className="text-green-500">+12%</span>
            <span className="text-gray-400 ml-1">ce mois</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-gray-800 rounded-xl p-6 border border-gray-700"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Produits</p>
              <p className="text-2xl font-bold text-white">{stats.totalProducts}</p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <Package size={24} className="text-green-500" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp size={16} className="text-green-500 mr-1" />
            <span className="text-green-500">+8%</span>
            <span className="text-gray-400 ml-1">ce mois</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-gray-800 rounded-xl p-6 border border-gray-700"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Commandes</p>
              <p className="text-2xl font-bold text-white">{stats.totalOrders}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
              <ShoppingCart size={24} className="text-yellow-500" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp size={16} className="text-green-500 mr-1" />
            <span className="text-green-500">+15%</span>
            <span className="text-gray-400 ml-1">ce mois</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-gray-800 rounded-xl p-6 border border-gray-700"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Revenus</p>
              <p className="text-2xl font-bold text-white">{stats.totalRevenue.toLocaleString('fr-FR')} €</p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <DollarSign size={24} className="text-green-500" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp size={16} className="text-green-500 mr-1" />
            <span className="text-green-500">+23%</span>
            <span className="text-gray-400 ml-1">ce mois</span>
          </div>
        </motion.div>
      </div>

      {/* Low Stock Alerts */}
      {lowStock.count > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="bg-gray-800 rounded-xl p-6 border border-gray-700"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-white">Alertes Stock Faible</h3>
            <span className="px-2 py-1 text-xs rounded-full bg-red-600 text-white">{lowStock.count}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-2 px-3 text-gray-400 text-sm">Produit</th>
                  <th className="text-left py-2 px-3 text-gray-400 text-sm">SKU</th>
                  <th className="text-left py-2 px-3 text-gray-400 text-sm">Stock</th>
                  <th className="text-left py-2 px-3 text-gray-400 text-sm">Seuil</th>
                </tr>
              </thead>
              <tbody>
                {lowStock.products.map((p) => (
                  <tr key={p.id} className="border-b border-gray-700">
                    <td className="py-2 px-3 text-white">{p.name}</td>
                    <td className="py-2 px-3 text-gray-300">{p.sku || '-'}</td>
                    <td className="py-2 px-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${p.in_stock <= 0 ? 'bg-red-600 text-white' : 'bg-yellow-600 text-white'}`}>
                        {p.in_stock}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-gray-300">{p.min_stock_level}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="bg-gray-800 rounded-xl p-6 border border-gray-700"
      >
        <h3 className="text-lg font-semibold text-white mb-4">Actions Rapides</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/admin/products"
            className="flex items-center p-4 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors duration-200 group"
          >
            <Package size={20} className="text-yellow-500 mr-3" />
            <div>
              <p className="font-medium text-white group-hover:text-yellow-500">Gérer les Produits</p>
              <p className="text-sm text-gray-400">Ajouter, modifier, supprimer</p>
            </div>
          </Link>

          <Link
            to="/admin/orders"
            className="flex items-center p-4 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors duration-200 group"
          >
            <ShoppingCart size={20} className="text-yellow-500 mr-3" />
            <div>
              <p className="font-medium text-white group-hover:text-yellow-500">Gérer les Commandes</p>
              <p className="text-sm text-gray-400">Suivre et traiter</p>
            </div>
          </Link>

          <Link
            to="/admin/users"
            className="flex items-center p-4 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors duration-200 group"
          >
            <Users size={20} className="text-yellow-500 mr-3" />
            <div>
              <p className="font-medium text-white group-hover:text-yellow-500">Gérer les Utilisateurs</p>
              <p className="text-sm text-gray-400">Clients et permissions</p>
            </div>
          </Link>

          <Link
            to="/admin/demo-photos"
            className="flex items-center p-4 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors duration-200 group"
          >
            <Image size={20} className="text-yellow-500 mr-3" />
            <div>
              <p className="font-medium text-white group-hover:text-yellow-500">Gérer les Photos Démo</p>
              <p className="text-sm text-gray-400">Bannières et carousel</p>
            </div>
          </Link>
        </div>
      </motion.div>

      {/* Recent Orders */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}
        className="bg-gray-800 rounded-xl p-6 border border-gray-700"
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-white">Commandes Récentes</h3>
          <Link
            to="/admin/orders"
            className="text-yellow-500 hover:text-yellow-400 text-sm font-medium"
          >
            Voir toutes →
          </Link>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Commande</th>
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Client</th>
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Montant</th>
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Statut</th>
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Date</th>
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.map((order) => (
                <tr key={order.id} className="border-b border-gray-700 hover:bg-gray-700/50">
                  <td className="py-3 px-4 text-white font-medium">{order.orderNumber}</td>
                  <td className="py-3 px-4 text-gray-300">{order.customerName}</td>
                  <td className="py-3 px-4 text-yellow-500 font-semibold">{order.amount.toLocaleString('fr-FR')} €</td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${getStatusColor(order.status)}`}>
                      {getStatusText(order.status)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-400">{new Date(order.date).toLocaleDateString('fr-FR')}</td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => { setSelectedOrder(order); setShowOrderModal(true); }}
                      className="text-yellow-500 hover:text-yellow-400 transition-colors"
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
      
      {showOrderModal && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700 shadow-xl">
            <div className="p-5 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                Commande #{selectedOrder.orderNumber}
              </h3>
              <button
                onClick={() => { setShowOrderModal(false); setSelectedOrder(null); }}
                className="text-gray-400 hover:text-white"
                aria-label="Fermer"
              >
                ✕
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm">Client</span>
                <span className="text-white font-medium">{selectedOrder.customerName || '-'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm">Montant</span>
                <span className="text-yellow-500 font-semibold">{Number(selectedOrder.amount || 0).toLocaleString('fr-FR')} €</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm">Statut</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ">
                  {selectedOrder.status}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm">Date</span>
                <span className="text-white">{new Date(selectedOrder.date).toLocaleDateString('fr-FR')}</span>
              </div>
            </div>
            <div className="p-5 border-t border-gray-700 flex justify-end space-x-3">
              <a
                href={`/admin/orders?orderId=${selectedOrder.id}`}
                className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-400 transition-colors"
              >
                Voir plus
              </a>
              <button
                onClick={() => { setShowOrderModal(false); setSelectedOrder(null); }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage; 